import * as THREE from 'three';
import { Subject } from 'rxjs';
import { Utils } from './utils';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Wireframe } from 'three/examples/jsm/lines/Wireframe.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { SheetHelper } from './sheet-helper';

export interface InteractionHandlers {
    mouseEnter?: (object: THREE.Object3D) => void;
    mouseLeave?: (object: THREE.Object3D) => void;
    click?: (object: THREE.Object3D, event: MouseEvent) => void;
    doubleClick?: (object: THREE.Object3D, event: MouseEvent) => void;
    mouseDown?: (object: THREE.Object3D, event: MouseEvent) => void;
    mouseUp?: (object: THREE.Object3D, event: MouseEvent) => void;
    mouseMove?: (object: THREE.Object3D, event: MouseEvent) => void;
}

export interface SceneClickDependencies {
    // Core objects
    scene: THREE.Scene;
    camera: THREE.Camera;

    // State management
    mousedrag: boolean;
    setMousedrag: (value: boolean) => void;

    // Project/sheet related
    selectedprojectpath: string;
    selectedSubprojectPath: string | null;
    sheetid: number;
    getSheetid: () => number;
    selectedprojectpath_setter: (value: string) => void;

    // Sprite management
    hoveredFaceCenterSprite: THREE.Sprite | null;
    hoveredFaceCenterOriginalMaterial: THREE.SpriteMaterial | null;
    selectedFaceCenterSprite: THREE.Sprite | null;
    setHoveredFaceCenterSprite: (sprite: THREE.Sprite | null) => void;
    setHoveredFaceCenterOriginalMaterial: (material: THREE.SpriteMaterial | null) => void;
    setSelectedFaceCenterSprite: (sprite: THREE.Sprite | null) => void;

    // Input/Output objects
    inputSprite: any;
    inputPoint: any;
    inputPosition: any;
    setInputSprite: (sprite: any) => void;
    setInputPoint: (point: any) => void;
    setInputPosition: (position: any) => void;

    // Selection state
    selectedNode: any;
    selectedSubprojectID: string | null;
    selectedProjectName: string | null;
    selectedObject: any;
    sheetNode: any;
    sheetSelected: boolean;
    isMainConfig: boolean;
    uihead: string;
    setSelectedNode: (node: any) => void;
    setSelectedSubprojectID: (id: string | null) => void;
    setSelectedProjectName: (name: string | null) => void;
    setSelectedObject: (obj: any) => void;
    setSheetNode: (node: any) => void;
    setSheetSelected: (selected: boolean) => void;
    setIsMainConfig: (isMain: boolean) => void;
    setUihead: (head: string) => void;

    // Data and formulas
    excels: any;
    PROJECT: string;
    cellformulas: any;
    dataset: any;
    dataset2?: any;
    project: any;
    jsongui: any;
    jsongui2: any;
    selectedObjectHelper: any;

    // Setters for data
    setCellformulas: (formulas: any) => void;
    setDataset: (data: any) => void;
    setDataset2: (data: any) => void;
    setProject: (proj: any) => void;
    setJsongui: (gui: any) => void;
    setJsongui2: (gui: any) => void;
    setSelectedObjectHelper: (helper: any) => void;

    // UI elements
    hot: any;
    isEditMode: boolean;
    editorComponent: any; // Reference to the EditorComponent for direct property updates

    // Scene action menu properties
    sceneactionleft: string;
    sceneactiontop: string;
    sceneactionvisible: boolean;
    sceneactionmenuvisible: boolean;
    setSceneactionleft: (left: string) => void;
    setSceneactiontop: (top: string) => void;
    setSceneactionvisible: (visible: boolean) => void;
    setSceneactionmenuvisible: (visible: boolean) => void;

    // Methods
    updateThreeFrame: () => void;
    showClipboardLabelWithText: (text: string) => void;
    handleDimensionTextClick: (object: any) => Promise<void>;
    handleObjectSelectionByRows: (row: number) => void;
    cellChanges2dataset: (changes: any[]) => void;
    updateModel: (changes: any, oldData: any, scene: THREE.Scene) => Promise<void>;
    subprojectSelected: () => Promise<void>;
    createConfigModel: () => Promise<any>;
    csschange: (css: string, isSubconfig?: boolean) => void;
    triggerChangeDetection: () => void;
    updateBreadcrumbs: () => void;
    adjustMenuPosition: (clientX: number, clientY: number) => { left: string; top: string };
}

export class InteractionManager {
    private raycaster: THREE.Raycaster;
    private mouse: THREE.Vector2;
    private interactiveObjects: Set<THREE.Object3D>;
    private hoveredObject: THREE.Object3D | null;
    private selectedObject: THREE.Object3D | null;
    private container: HTMLElement;
    private lastClickTime: number = 0;
    private readonly DOUBLE_CLICK_DELAY = 300; // ms
    private _cursorType: string = 'default';
    public cursorTypeChange = new Subject<string>();

    // Dependencies for sceneClick
    private dependencies: SceneClickDependencies | null = null;

    // Add properties for hover highlighting
    private hoveredObjectHelper: any = null;
    private lastHoveredObject: THREE.Object3D | null = null;

    // Mouse drag tracking to prevent selection during camera movements
    private mouseDownPosition: { x: number, y: number } | null = null;
    private isDragging: boolean = false;
    private dragCooldownUntil: number = 0; // Timestamp to prevent selection after drag
    private readonly DRAG_THRESHOLD = 5; // pixels - minimum movement to consider it a drag
    private readonly DRAG_COOLDOWN = 100; // ms - cooldown period after drag ends

    get cursorType(): string {
        return this._cursorType;
    }

    set cursorType(value: string) {
        if (this._cursorType !== value) {
            this._cursorType = value;
            this.cursorTypeChange.next(value);
            document.body.style.cursor = value;
        }
    }

    /**
     * Check if we should ignore interaction events due to recent dragging
     */
    private shouldIgnoreInteraction(): boolean {
        const now = Date.now();
        const recentDrag = now < this.dragCooldownUntil;
        const currentlyDragging = this.isDragging;

        if (recentDrag || currentlyDragging) {
            console.log('[InteractionManager] Ignoring interaction - isDragging:', currentlyDragging, 'recentDrag:', recentDrag, 'cooldownRemaining:', Math.max(0, this.dragCooldownUntil - now));
            return true;
        }
        return false;
    }

    constructor(
        private scene: THREE.Scene,
        private camera: THREE.Camera,
        private renderer: THREE.WebGLRenderer,
        cursorType: string = 'default'
    ) {
        this._cursorType = cursorType;
        console.log('Initializing InteractionManager');
        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();
        this.interactiveObjects = new Set();
        this.hoveredObject = null;
        this.selectedObject = null;
        this.container = this.renderer.domElement;

        // Bind event handlers
        this.handleMouseMove = this.handleMouseMove.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handleMouseDown = this.handleMouseDown.bind(this);
        this.handleMouseUp = this.handleMouseUp.bind(this);
        this.handleSceneClick = this.handleSceneClick.bind(this);

        // Add event listeners
        this.container.addEventListener('mousemove', this.handleMouseMove);
        this.container.addEventListener('click', this.handleClick);
        this.container.addEventListener('mousedown', this.handleMouseDown);
        this.container.addEventListener('mouseup', this.handleMouseUp);
        this.container.addEventListener('mouseleave', this.handleMouseLeave.bind(this));

        // Add scene click listener
        this.container.addEventListener('dblclick', this.handleSceneClick);
    }

    setDependencies(dependencies: SceneClickDependencies) {
        console.log('[InteractionManager] Setting dependencies', {
            hasScene: !!dependencies.scene,
            hasCamera: !!dependencies.camera,
            hasExcels: !!dependencies.excels,
            PROJECT: dependencies.PROJECT,
            sheetid: dependencies.sheetid,
            selectedprojectpath: dependencies.selectedprojectpath,
            isEditMode: dependencies.isEditMode,
            dependencyCount: Object.keys(dependencies).length
        });
        this.dependencies = dependencies;

        // ISSUE 5: Automatically register important scene objects for interaction
        this.autoRegisterSceneObjects();

        console.log('[InteractionManager] Dependencies set successfully, interactive objects count:', this.interactiveObjects.size);
    }

    /**
     * Automatically register important scene objects for interaction
     * This helps ensure objects are selectable even if not manually registered
     */
    private autoRegisterSceneObjects() {
        if (!this.dependencies?.scene) return;

        console.log('[InteractionManager] Auto-registering scene objects for interaction');
        let registeredCount = 0;

        this.dependencies.scene.traverse((object) => {
            // Register objects that should be interactive
            const shouldRegister =
                // Objects with project/sheet types
                (object.userData?.typ?.toUpperCase().startsWith('PROJECT')) ||
                (object.userData?.typ?.toUpperCase().startsWith('SHEET')) ||
                // Objects with row data (3D primitives)
                (object.userData?.rowindex !== undefined && object.userData?.sheetid !== undefined) ||
                // Mesh objects that are not helpers (including those with UUID names)
                (object instanceof THREE.Mesh && !object.userData?.isHelper) ||
                // GLTF objects and their children
                (object.userData?.isGltf || this.isPartOfGltfObject(object)) ||
                // Objects that are children of interactive parents
                (this.hasValidParentUserData(object) && object instanceof THREE.Mesh) ||
                // Connector objects
                (object.userData?.isConnector) ||
                // Face center objects
                (object.userData?.type === 'refpoint');

            if (shouldRegister && !this.interactiveObjects.has(object)) {
                this.add(object);
                registeredCount++;

                // Debug log for UUID-named objects
                if (object.name && object.name.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
                    console.log('[InteractionManager] Registered UUID-named object:', {
                        name: object.name,
                        type: object.constructor.name,
                        hasUserData: !!object.userData,
                        isGltf: object.userData?.isGltf,
                        parentName: object.parent?.name
                    });
                }
            }
        });

        console.log(`[InteractionManager] Auto-registered ${registeredCount} scene objects for interaction`);
    }

    /**
     * Public method to refresh interactive object registration
     * Call this after scene updates or model changes
     */
    public refreshInteractiveObjects() {
        console.log('[InteractionManager] Refreshing interactive object registration');
        this.autoRegisterSceneObjects();
    }

    add(object: THREE.Object3D, handlers: InteractionHandlers = {}) {
        console.log('Adding interactive object:', object.name || object.uuid, {
            isConnector: object.userData?.isConnector,
            isSprite: object instanceof THREE.Sprite,
            hasGeometry: !!(object as any).geometry,
            isGroup: object instanceof THREE.Group
        });
        object.userData.handlers = handlers;
        this.interactiveObjects.add(object);

        // Special debug for connectors
        if (object.userData?.isConnector) {
            console.log('[InteractionManager] Added connector to interactive objects:', object.userData.connectorLabel);
        }
    }

    remove(object: THREE.Object3D) {
        console.log('Removing interactive object:', object.name || object.uuid);
        this.interactiveObjects.delete(object);
        if (this.hoveredObject === object) {
            console.log('Cleared hovered object reference');
            this.hoveredObject = null;
        }
        if (this.selectedObject === object) {
            console.log('Cleared selected object reference');
            this.selectedObject = null;
        }
    }

    private updateMousePosition(event: MouseEvent) {
        const rect = this.container.getBoundingClientRect();
        this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        //  console.debug('Mouse position updated:', { x: this.mouse.x, y: this.mouse.y });
    }

    private getIntersectedObject(event: MouseEvent): THREE.Object3D | null {
        this.updateMousePosition(event);
        this.raycaster.setFromCamera(this.mouse, this.camera);

        // Configure raycaster properly for sprites (same as sceneClick method)
        this.raycaster.params.Sprite = {};
        this.raycaster.params.Points = { threshold: 1 };

        // ISSUE 1: Only checking interactiveObjects instead of all scene objects
        // This limits selection to manually registered objects only
        // FIX: Also check scene children for general object selection
        let intersectsFromInteractive = [];
        let intersectsFromScene = [];

        if (this.interactiveObjects.size > 0) {
            intersectsFromInteractive = this.raycaster.intersectObjects([...this.interactiveObjects], true);
        }

        // Always check scene objects for general selection
        if (this.dependencies?.scene) {
            intersectsFromScene = this.raycaster.intersectObjects(this.dependencies.scene.children, true);
        }

        // Combine both intersection results
        const allIntersects = [...intersectsFromInteractive, ...intersectsFromScene];

        console.debug('Intersection test results:', {
            interactiveResults: intersectsFromInteractive.length,
            sceneResults: intersectsFromScene.length,
            totalResults: allIntersects.length,
            interactiveObjectsCount: this.interactiveObjects.size
        });

        // Remove duplicates based on object UUID
        const uniqueIntersects = allIntersects.filter((intersect, index, array) =>
            array.findIndex(item => item.object.uuid === intersect.object.uuid) === index
        );

        // Separate connectors and other objects, prioritize connectors
        let closestConnector: THREE.Object3D | null = null;
        let closestConnectorDistance = Infinity;
        let closestOther: THREE.Object3D | null = null;
        let closestOtherDistance = Infinity;

        for (const intersect of uniqueIntersects) {
            const obj = intersect.object;
            const distance = intersect.distance;

            // Skip helper objects, invisible objects, and baseScene objects
            if ((obj.userData && obj.userData.isHelper) ||
                !obj.visible ||
                obj.name === "baseScene" ||
                obj.name === "hemilighthelper") {
                continue;
            }

            // Check if this is a connector object or connector sprite
            const isConnectorObj = obj.userData?.isConnector ||
                obj.userData?.isConnectorArrow ||
                obj.userData?.isConnectorSphere ||
                obj.parent?.userData?.isConnector;

            if (isConnectorObj) {
                if (distance < closestConnectorDistance) {
                    closestConnector = obj;
                    closestConnectorDistance = distance;
                    console.debug('Found connector intersection:', obj.userData?.connectorLabel || obj.parent?.userData?.connectorLabel);
                }
            } else {
                if (distance < closestOtherDistance) {
                    closestOther = obj;
                    closestOtherDistance = distance;
                }
            }
        }

        // Return closest connector first, then closest other object
        const result = closestConnector || closestOther;
        if (result) {
            console.debug('Found valid intersection with:', result.name || result.uuid, {
                isConnector: result.userData?.isConnector,
                parentIsConnector: result.parent?.userData?.isConnector
            });
        } else {
            console.debug('No valid intersections found');
        }

        return result;
    }

    private handleMouseMove(event: MouseEvent) {
        // Check for dragging if mouse is down
        if (this.mouseDownPosition && !this.isDragging) {
            const deltaX = Math.abs(event.clientX - this.mouseDownPosition.x);
            const deltaY = Math.abs(event.clientY - this.mouseDownPosition.y);
            const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

            if (distance > this.DRAG_THRESHOLD) {
                this.isDragging = true;
                console.log('[InteractionManager] Drag detected, distance:', distance);
            }
        }

        const intersectedObject = this.getIntersectedObject(event);

        if (intersectedObject) {
            // Check if this is a face center sphere and set cursor to pointer
            if (intersectedObject instanceof THREE.Mesh &&
                intersectedObject.userData.type === 'refpoint' &&
                intersectedObject.userData.isFaceCenter) {
                this.cursorType = 'pointer';
            }
            // Check if this is a face center sprite (legacy support)
            else if (intersectedObject instanceof THREE.Sprite &&
                intersectedObject.userData.type === 'refpoint') {
                this.cursorType = 'pointer';
            }
            // Check if this is a connector sprite
            else if (intersectedObject instanceof THREE.Sprite &&
                intersectedObject.userData.type === 'connector') {
                this.cursorType = 'pointer';
            }
            else {
                this.cursorType = 'default';
            }

            if (this.hoveredObject !== intersectedObject) {
                console.log('Hover changed:', {
                    from: this.getObjectDisplayName(this.hoveredObject) || 'none',
                    to: this.getObjectDisplayName(intersectedObject)
                });
                // Mouse Leave for previous object
                if (this.hoveredObject) {
                    const handlers = this.hoveredObject.userData?.handlers;
                    handlers?.mouseLeave?.(this.hoveredObject);
                }

                // Mouse Enter for new object
                const handlers = intersectedObject.userData?.handlers;
                handlers?.mouseEnter?.(intersectedObject);
                handlers?.mouseMove?.(intersectedObject, event);
                this.hoveredObject = intersectedObject;

                // Handle object highlighting for 3D objects
                this.handleObjectMouseOver(intersectedObject);
            } else {
                // Mouse Move on same object
                const handlers = intersectedObject.userData?.handlers;
                handlers?.mouseMove?.(intersectedObject, event);
            }
        } else if (this.hoveredObject) {
            // Mouse Leave when no intersection - reset cursor to default
            this.cursorType = 'default';

            // Mouse Leave when no intersection
            const handlers = this.hoveredObject.userData?.handlers;
            handlers?.mouseLeave?.(this.hoveredObject);
            this.hoveredObject = null;

            // Handle object highlighting cleanup
            this.handleObjectMouseOut();
        } else {
            // No intersection and no previous hover - ensure cursor is default
            this.cursorType = 'default';

            // Clear any remaining highlights when nothing is being hovered
            this.handleObjectMouseOut();
        }
    }

    private handleObjectMouseOver(object: THREE.Object3D) {
        if (!this.dependencies) return;

        // Skip if this is the same object as last time
        if (this.lastHoveredObject === object) return;

        // Clean up previous hover highlight
        this.handleObjectMouseOut();

        // Check if this object should be highlighted (similar to handleObjectSelectionByRows logic)
        if (this.shouldHighlightObject(object)) {
            this.createHoverHighlight(object);
            this.lastHoveredObject = object;
        }
    }

    private handleObjectMouseOut() {
        if (!this.dependencies) return;

        // Remove hover highlight
        if (this.hoveredObjectHelper) {
            this.dependencies.scene.remove(this.hoveredObjectHelper);
            this.hoveredObjectHelper = null;
        }
        this.lastHoveredObject = null;
    }

    /**
     * Public method to clear all selection highlights and helpers
     */
    public clearAllSelectionHighlights() {
        if (!this.dependencies) return;

        console.log('[InteractionManager] Clearing all selection highlights');

        // Clear hover highlight
        this.handleObjectMouseOut();

        // Clear main selection helper from editor component if it exists
        if (this.dependencies.selectedObjectHelper) {
            this.dependencies.scene.remove(this.dependencies.selectedObjectHelper);
            this.dependencies.setSelectedObjectHelper(null);
        }

        // Reset selected object references
        this.selectedObject = null;
        this.dependencies.setSelectedObject(null);

        // Clear any face center sprite selections
        if (this.dependencies.selectedFaceCenterSprite) {
            // Reset to original material if available
            if (this.dependencies.selectedFaceCenterSprite.userData.originalMaterial) {
                this.dependencies.selectedFaceCenterSprite.material = this.dependencies.selectedFaceCenterSprite.userData.originalMaterial;
            }
            this.dependencies.setSelectedFaceCenterSprite(null);
        }

        // Clear hovered face center sprite
        if (this.dependencies.hoveredFaceCenterSprite && this.dependencies.hoveredFaceCenterOriginalMaterial) {
            this.dependencies.hoveredFaceCenterSprite.material = this.dependencies.hoveredFaceCenterOriginalMaterial;
            this.dependencies.setHoveredFaceCenterSprite(null);
            this.dependencies.setHoveredFaceCenterOriginalMaterial(null);
        }

        // Update the frame to reflect changes
        this.dependencies.updateThreeFrame();
    }

    private shouldHighlightObject(object: THREE.Object3D): boolean {
        if (!this.dependencies) return false;

        // Skip sprites, helpers, invisible objects, and baseScene objects
        if (object instanceof THREE.Sprite ||
            (object.userData && object.userData.isHelper) ||
            object.name === "baseScene" ||
            object.name === "hemilighthelper" ||
            !object.visible) {
            return false;
        }

        // ENHANCED: More robust object highlighting criteria
        // First check: Objects without userData but are mesh objects should be highlightable
        if (!object.userData) {
            // Still allow highlighting for basic geometry objects
            if (object instanceof THREE.Mesh && object.geometry) {
                console.debug('[InteractionManager] Allowing highlight for mesh without userData:', object.name || object.uuid);
                return true;
            }
            return false;
        }

        // Second check: Objects with row data (CAD objects from spreadsheet)
        if (object.userData.rowindex !== undefined &&
            object.userData.sheetid !== undefined &&
            this.dependencies.dataset) {

            const row = object.userData.rowindex;
            const dataset = this.dependencies.dataset;

            // Check if this is a valid 3D object row (similar to handleObjectSelectionByRows)
            if (dataset[row] &&
                row > this.getOutputRow(dataset) &&
                dataset[row][0] &&
                (this.isInAutocompleteList(dataset[row][1]) || dataset[row][1]?.startsWith('project'))) {
                return true;
            }
        }

        // Third check: Objects with specific types (projects, sheets, etc.)
        if (object.userData.typ?.toUpperCase().startsWith('PROJECT') ||
            object.userData.typ?.toUpperCase().startsWith('SHEET')) {
            console.debug('[InteractionManager] Allowing highlight for object type:', object.userData.typ);
            return true;
        }

        // Fourth check: GLTF child objects (often have UUIDs as names)
        if (object.userData.isGltf || this.isPartOfGltfObject(object)) {
            console.debug('[InteractionManager] Allowing highlight for GLTF object:', object.name || object.uuid);
            return true;
        }

        // Fifth check: Basic mesh objects should be highlightable
        if (object instanceof THREE.Mesh && object.geometry) {
            console.debug('[InteractionManager] Allowing highlight for basic mesh:', object.name || object.uuid);
            return true;
        }

        // Sixth check: Objects that have a parent with proper userData (GLTF children)
        if (this.hasValidParentUserData(object)) {
            console.debug('[InteractionManager] Allowing highlight for object with valid parent:', object.name || object.uuid);
            return true;
        }

        return false;
    }

    /**
     * Check if object is part of a GLTF object hierarchy
     */
    private isPartOfGltfObject(object: THREE.Object3D): boolean {
        let current = object;
        while (current) {
            if (current.userData?.typ === 'gltf' || current.userData?.isGltf) {
                return true;
            }
            current = current.parent;
        }
        return false;
    }

    /**
     * Check if object has a parent with valid userData that makes it worth highlighting
     */
    private hasValidParentUserData(object: THREE.Object3D): boolean {
        let current = object.parent;
        while (current && current !== this.dependencies?.scene) {
            if (current.userData) {
                // Parent has CAD object data
                if (current.userData.rowindex !== undefined && current.userData.sheetid !== undefined) {
                    return true;
                }
                // Parent is a project or sheet
                if (current.userData.typ?.toUpperCase().startsWith('PROJECT') ||
                    current.userData.typ?.toUpperCase().startsWith('SHEET')) {
                    return true;
                }
                // Parent is a GLTF object
                if (current.userData.typ === 'gltf' || current.userData.isGltf) {
                    return true;
                }
            }
            current = current.parent;
        }
        return false;
    }

    /**
     * Get a meaningful display name for an object, handling UUIDs and providing context
     */
    private getObjectDisplayName(object: THREE.Object3D | null): string {
        if (!object) return 'none';

        const name = object.name || object.uuid;

        // Check if this is a UUID
        const isUUID = name.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);

        if (isUUID) {
            // For UUID objects, try to provide more meaningful context
            let context = '';

            // Check if it's a GLTF child
            if (object.userData?.isGltf || this.isPartOfGltfObject(object)) {
                context = ' (GLTF child)';
            }

            // Check if parent has a meaningful name
            if (object.parent && object.parent.name && !object.parent.name.match(/^[0-9a-f-]+$/i)) {
                context += ` -> ${object.parent.name}`;
            }

            // Check object type
            if (object instanceof THREE.Mesh) {
                context += ' [Mesh]';
            }

            return `${name.substring(0, 8)}...${context}`;
        }

        return name;
    }

    private getOutputRow(dataset: any[][]): number {
        // Simple implementation to find output row (you might need to adjust this)
        for (let i = 0; i < dataset.length; i++) {
            if (dataset[i] && dataset[i][0] === "OUTPUTID") {
                return i;
            }
        }
        return 0;
    }

    private isInAutocompleteList(type: string): boolean {
        // List of primitive types that should be highlighted (from editor component)
        const autocompleteListPrimitives = [
            '', 'line', 'dimensionline', 'shape', 'surface', 'extrusion', 'sphere', 'capsule',
            'cylinder', 'ibeam', 'cube', 'lathe', 'image3d', 'subtraction', 'textflat', 'gltf',
            'group', 'light', 'gltfnode', 'text2d', 'overlay', 'overlayhtml', 'rectangle2d',
            'image2d', 'chart', 'profile', 'connector', 'aluprofile', 'copy', 'grid', 'arrow',
            'tube', 'union', 'ring'
        ];
        return autocompleteListPrimitives.includes(type);
    }

    private createHoverHighlight(object: THREE.Object3D) {
        if (!this.dependencies) return;

        try {
            // Use the same bounding box calculation as the editor component
            const bbox = this.getBoundingBox(object);
            if (!bbox) return;

            const box = new THREE.Box3(bbox.min, bbox.max);
            const boxGeometry = new THREE.BoxGeometry(
                box.max.x - box.min.x,
                box.max.y - box.min.y,
                box.max.z - box.min.z
            );
            const center = new THREE.Vector3();
            bbox.getCenter(center);

            const edgesGeometry = new THREE.EdgesGeometry(boxGeometry);

            // Import LineMaterial and related classes
            const lineGeometry = new LineSegmentsGeometry().fromEdgesGeometry(edgesGeometry);
            const lineMaterial = new LineMaterial({
                color: 0xff00ff,  // Same color as selection
                linewidth: 4,     // Same line width as selection
                resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
                transparent: true,
                opacity: 0.2,     // Lower opacity for hover (0.2 instead of 0.3)
                dashed: true,     // Enable dashed lines
                dashSize: 5,      // Updated dash size to 5
                gapSize: 5        // Updated gap size to 5
            });

            const wireframe = new Wireframe(lineGeometry, lineMaterial);
            wireframe.position.copy(center);
            wireframe.scale.set(1.1, 1.1, 1.1);
            wireframe.computeLineDistances();

            // Mark as helper to avoid interference
            wireframe.userData.isHelper = true;

            this.dependencies.scene.add(wireframe);
            this.hoveredObjectHelper = wireframe;

        } catch (error) {
            console.warn('Error creating hover highlight:', error);
        }
    }

    private getBoundingBox(object: THREE.Object3D): any {
        // This is a simplified bounding box calculation
        // You might need to use the same ThreeUtils.getBBox method used in the editor
        try {
            const box = new THREE.Box3().setFromObject(object);
            return {
                min: box.min,
                max: box.max,
                getCenter: (target: any) => box.getCenter(target)
            };
        } catch (error) {
            console.warn('Error calculating bounding box:', error);
            return null;
        }
    }

    private handleClick(event: MouseEvent) {
        // Check if we should ignore this interaction due to dragging or recent drag
        if (this.shouldIgnoreInteraction()) {
            return;
        }

        const intersectedObject = this.getIntersectedObject(event);
        const currentTime = Date.now();

        if (intersectedObject) {
            console.log('Click detected on:', intersectedObject.name || intersectedObject.uuid);
            const handlers = intersectedObject.userData?.handlers;

            if (this.selectedObject === intersectedObject &&
                currentTime - this.lastClickTime < this.DOUBLE_CLICK_DELAY) {
                console.log('Double click detected');
                handlers?.doubleClick?.(intersectedObject, event);
            } else {
                console.log('Single click detected');
                handlers?.click?.(intersectedObject, event);
            }

            this.selectedObject = intersectedObject;
            this.lastClickTime = currentTime;
        }
    }

    private handleMouseDown(event: MouseEvent) {
        // Store initial mouse position for drag detection
        this.mouseDownPosition = { x: event.clientX, y: event.clientY };
        this.isDragging = false;

        console.log('[InteractionManager] Mouse down at:', this.mouseDownPosition);

        const intersectedObject = this.getIntersectedObject(event);
        if (intersectedObject) {
            console.log('Mouse down on:', intersectedObject.name || intersectedObject.uuid);
            const handlers = intersectedObject.userData?.handlers;
            handlers?.mouseDown?.(intersectedObject, event);
        }
    }

    private handleMouseUp(event: MouseEvent) {
        console.log('[InteractionManager] Mouse up - isDragging:', this.isDragging);

        // Reset mouse tracking
        const wasDragging = this.isDragging;
        this.mouseDownPosition = null;
        this.isDragging = false;

        // Set cooldown period if we were dragging to prevent immediate selection
        if (wasDragging) {
            this.dragCooldownUntil = Date.now() + this.DRAG_COOLDOWN;
            console.log('[InteractionManager] Drag ended - setting cooldown until:', this.dragCooldownUntil);
            return;
        }

        // Check if we should ignore this interaction due to recent dragging
        if (this.shouldIgnoreInteraction()) {
            return;
        }

        const intersectedObject = this.getIntersectedObject(event);
        if (intersectedObject) {
            console.log('Mouse up on:', intersectedObject.name || intersectedObject.uuid);
            const handlers = intersectedObject.userData?.handlers;
            handlers?.mouseUp?.(intersectedObject, event);
        }
    }

    private handleMouseLeave(event: MouseEvent) {
        console.log('[InteractionManager] Mouse leave - resetting drag state');

        // Reset drag state when mouse leaves the container
        this.mouseDownPosition = null;
        this.isDragging = false;

        // Also clear any hover state
        if (this.hoveredObject) {
            const handlers = this.hoveredObject.userData?.handlers;
            handlers?.mouseLeave?.(this.hoveredObject);
            this.hoveredObject = null;
            this.handleObjectMouseOut();
        }
    }

    private async handleSceneClick(e: MouseEvent) {
        console.log('[InteractionManager] handleSceneClick triggered', {
            eventType: e.type,
            hasDependencies: !!this.dependencies,
            clientX: e.clientX,
            clientY: e.clientY,
            isDragging: this.isDragging,
            timestamp: Date.now()
        });

        // Check if we should ignore this interaction due to dragging or recent drag
        if (this.shouldIgnoreInteraction()) {
            return;
        }

        if (!this.dependencies) {
            console.warn('[InteractionManager] SceneClick dependencies not set');
            return;
        }

        try {
            await this.sceneClick(e);
            console.log('[InteractionManager] handleSceneClick completed successfully');
        } catch (error) {
            console.error('[InteractionManager] Error in handleSceneClick:', error);
            throw error;
        }
    }

    async sceneClick(e: MouseEvent) {
        console.log('[InteractionManager] sceneClick method called', {
            eventType: e.type,
            layerX: e.layerX,
            layerY: e.layerY,
            hasDependencies: !!this.dependencies,
            timestamp: Date.now()
        });

        if (!this.dependencies) {
            console.warn('[InteractionManager] SceneClick dependencies not set');
            return;
        }

        const deps = this.dependencies;

        console.log('[InteractionManager] Starting sceneClick processing with state:', {
            mousedrag: deps.mousedrag,
            selectedprojectpath: deps.selectedprojectpath,
            selectedSubprojectPath: deps.selectedSubprojectPath,
            sheetid: deps.getSheetid(),
            isMainConfig: deps.isMainConfig,
            hasSelectedNode: !!deps.selectedNode,
            hasSelectedObject: !!deps.selectedObject
        });

        console.log('[DEBUG] sceneClick called with event:', e);
        console.log('[DEBUG] mousedrag state:', deps.mousedrag);
        console.log('[DEBUG] InteractionManager isDragging:', this.isDragging);

        // Check if we should ignore this interaction due to dragging or recent drag
        if (this.shouldIgnoreInteraction()) {
            return;
        }

        // Reset mousedrag flag but don't return early - double clicks should still be processed
        // The mousedrag flag might be set from the preceding single click event
        if (deps.mousedrag) {
            console.log('[DEBUG] Mouse drag was true, resetting but continuing with click processing');
            deps.setMousedrag(false);
        }
        var selectRoot = false;
        // Get mouse position and setup raycaster
        const canvasTarget = document.getElementById('threecontainer');
        console.log('[InteractionManager] Canvas target found:', {
            hasCanvasTarget: !!canvasTarget,
            canvasWidth: canvasTarget?.clientWidth,
            canvasHeight: canvasTarget?.clientHeight,
            layerX: e.layerX,
            layerY: e.layerY
        });

        var mouse = new THREE.Vector2();
        mouse.x = (e.layerX / canvasTarget.clientWidth) * 2 - 1;
        mouse.y = -(e.layerY / canvasTarget.clientHeight) * 2 + 1;
        console.log('[InteractionManager] Mouse coordinates normalized:', { x: mouse.x, y: mouse.y });

        var raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(mouse, deps.camera);

        // Configure raycaster for better sprite detection (same as handleSceneEvent)
        raycaster.params.Sprite = {};
        raycaster.params.Points = { threshold: 1 };
        raycaster.ray.origin.addScaledVector(
            raycaster.ray.direction,
            (deps.camera as THREE.PerspectiveCamera | THREE.OrthographicCamera).near
        );
        raycaster.layers.enableAll();

        // Get intersected objects - use the same approach as handleSceneEvent
        console.log('[InteractionManager] Project paths:', {
            selectedprojectpath: deps.selectedprojectpath,
            selectedSubprojectPath: deps.selectedSubprojectPath
        });

        // First try to intersect with the full scene like handleSceneEvent does
        var intersects = raycaster.intersectObjects(deps.scene.children, true);
        console.log('[InteractionManager] Raycasting results:', {
            fullSceneIntersects: intersects?.length,
            sceneChildrenCount: deps.scene.children.length,
            intersectedObjects: intersects?.slice(0, 3).map(i => ({
                objectName: i.object.name,
                objectType: i.object.constructor.name,
                distance: i.distance,
                isSprite: i.object instanceof THREE.Sprite,
                userData: i.object.userData
            }))
        });

        // Also try the original logic for comparison
        var r = deps.selectedprojectpath;
        if (deps.selectedprojectpath == "root^$" + deps.getSheetid()) r = "root" // #TODO: sheet1 funzt net
        var proobj = deps.scene.getObjectByName(deps.selectedSubprojectPath);
        console.log('[InteractionManager] Project object search:', {
            selectedSubprojectPath: deps.selectedSubprojectPath,
            proobj: !!proobj,
            probjName: proobj?.name,
            rValue: r
        });

        if (proobj) {
            var proobj_intersects = raycaster.intersectObjects(proobj?.children, true);
            console.log('[InteractionManager] Project object intersects:', {
                intersectsCount: proobj_intersects?.length,
                probjChildrenCount: proobj.children.length
            });

            if (proobj_intersects.length == 0) {
                selectRoot = true;
                console.log('[InteractionManager] No project object intersects, setting selectRoot = true');
            }
        } else {
            selectRoot = true;
            console.log('[InteractionManager] No proobj found, setting selectRoot = true');
        }

        // Check for sprite intersections first (same logic as handleSceneEvent)
        console.log('[InteractionManager] Processing intersected objects:', {
            totalIntersects: intersects?.length,
            selectRoot: selectRoot
        });

        let firstIntersectedObject: THREE.Object3D | null = null;
        if (intersects && intersects.length > 0) {
            let closestSprite: THREE.Object3D | null = null;
            let closestSpriteDistance = Infinity;
            let closestNonSprite: THREE.Object3D | null = null;
            let closestNonSpriteDistance = Infinity;

            // Log all intersected objects for debugging
            console.log('[InteractionManager] Detailed intersect analysis:');
            intersects.forEach((intersect, index) => {
                const obj = intersect.object;
                console.log(`[InteractionManager] Intersect ${index}:`, {
                    name: obj.name,
                    type: obj.constructor.name,
                    isSprite: obj instanceof THREE.Sprite,
                    spriteType: obj.userData?.type,
                    distance: intersect.distance,
                    visible: obj.visible,
                    isHelper: obj.userData?.isHelper,
                    hasOutputId: !!obj.userData?.outputid,
                    hasIndex: obj.userData?.index !== undefined,
                    isConnector: obj.userData?.isConnector,
                    isConnectorArrow: obj.userData?.isConnectorArrow,
                    isConnectorSphere: obj.userData?.isConnectorSphere,
                    parentIsConnector: obj.parent?.userData?.isConnector,
                    isBaseScene: obj.name === "baseScene",
                    isHemiLightHelper: obj.name === "hemilighthelper",
                    isSlicerPlane: obj.userData?.isSlicerPlane,
                    isPlaneHelper: obj instanceof THREE.PlaneHelper,
                    parentName: obj.parent?.name,
                    parentType: obj.parent?.constructor.name,
                    userData: obj.userData
                });
            });

            for (const intersect of intersects) {
                const object = intersect.object;
                const distance = intersect.distance;

                // Always allow sprites with type 'refpoint' to be selectable
                if (object instanceof THREE.Sprite && object.userData?.type === 'refpoint') {
                    console.log('[InteractionManager] Found refpoint sprite at distance:', distance);
                    if (distance < closestSpriteDistance) {
                        closestSprite = object;
                        closestSpriteDistance = distance;
                        console.log('[InteractionManager] Set as closest refpoint sprite');
                    }
                }
                // Check for face center spheres (THREE.Mesh with refpoint type and isFaceCenter) - treat with same priority as sprites
                else if (object instanceof THREE.Mesh && object.userData?.type === 'refpoint' && object.userData?.isFaceCenter) {
                    console.log('[InteractionManager] Found face center sphere at distance:', distance);
                    if (distance < closestSpriteDistance) {
                        closestSprite = object; // Treat face center spheres with same priority as sprites
                        closestSpriteDistance = distance;
                        console.log('[InteractionManager] Set as closest face center sphere (treated as sprite priority)');
                    }
                }
                // Check for connector sprites
                else if (object instanceof THREE.Sprite && object.userData?.type === 'connector') {
                    if (distance < closestSpriteDistance) {
                        closestSprite = object;
                        closestSpriteDistance = distance;
                        console.log('[InteractionManager] Set as closest connector sprite:', {
                            spriteType: object.userData?.type,
                            distance: distance,
                            label: object.userData?.label
                        });
                    }
                }
                // Check for other sprite types
                else if (object instanceof THREE.Sprite) {
                    console.log('[InteractionManager] Found other sprite type:', {
                        spriteType: object.userData?.type || 'no type',
                        distance: distance
                    });
                }
                // Check for connector objects (these should have high priority)
                else if (object.userData?.isConnector || object.userData?.isConnectorArrow || object.userData?.isConnectorSphere || object.parent?.userData?.isConnector) {
                    if (distance < closestNonSpriteDistance) {
                        closestNonSprite = object;
                        closestNonSpriteDistance = distance;
                        console.log('[InteractionManager] Set new closest connector object:', {
                            name: object.name,
                            type: object.constructor.name,
                            distance: distance,
                            isConnector: object.userData?.isConnector,
                            isConnectorChild: object.userData?.isConnectorArrow || object.userData?.isConnectorSphere,
                            parentIsConnector: object.parent?.userData?.isConnector
                        });
                    }
                }
                // For non-sprites, check visibility, helper status, and baseScene objects
                else if (object.visible &&
                    (!object.userData || !object.userData.isHelper) &&
                    object.name !== "baseScene" &&
                    object.name !== "hemilighthelper") {
                    if (distance < closestNonSpriteDistance) {
                        closestNonSprite = object;
                        closestNonSpriteDistance = distance;
                        console.log('[InteractionManager] Set new closest non-sprite:', {
                            name: object.name,
                            type: object.constructor.name,
                            distance: distance
                        });
                    }
                }
            }

            // Prioritize sprites
            if (closestSprite) {
                firstIntersectedObject = closestSprite;
                console.log('[InteractionManager] Selected closest sprite as firstIntersectedObject:', {
                    name: closestSprite.name,
                    type: closestSprite.constructor.name,
                    spriteType: closestSprite.userData?.type,
                    distance: closestSpriteDistance
                });
            } else {
                firstIntersectedObject = closestNonSprite;
                console.log('[InteractionManager] Selected closest non-sprite as firstIntersectedObject:', {
                    name: closestNonSprite?.name,
                    type: closestNonSprite?.constructor.name,
                    distance: closestNonSpriteDistance
                });
            }
        } else {
            console.log('[InteractionManager] No intersects found');
        }

        // Handle face center sphere and sprite double-clicks (same selection logic as handleSceneEvent MouseDown)
        console.log('[DEBUG] Checking firstIntersectedObject for face center handling:', {
            exists: !!firstIntersectedObject,
            isSprite: firstIntersectedObject instanceof THREE.Sprite,
            isMesh: firstIntersectedObject instanceof THREE.Mesh,
            userData: firstIntersectedObject?.userData,
            type: firstIntersectedObject?.userData?.type
        });

        // Object menu sprite handling is now done via HTML element in the editor component
        // No need to handle object_menu sprites here anymore

        // Handle connector sprite clicks
        if (firstIntersectedObject && firstIntersectedObject instanceof THREE.Sprite && firstIntersectedObject.userData.type === 'connector') {
            console.log('[DEBUG] Processing connector sprite click');

            // Get the connector group (parent of the sprite)
            const connectorGroup = firstIntersectedObject.parent;

            if (connectorGroup && connectorGroup.userData?.isConnector) {
                console.log('[DEBUG] Found connector group from sprite:', connectorGroup.userData.connectorLabel);

                // Emit connector selection event with mouse coordinates
                if (typeof window !== 'undefined' && (window as any).dispatchEvent) {
                    const customEvent = new CustomEvent('connectorSelected', {
                        detail: {
                            label: connectorGroup.userData.connectorLabel,
                            outputid: connectorGroup.userData.outputid,
                            position: connectorGroup.userData.position,
                            normal: connectorGroup.userData.normal,
                            type: connectorGroup.userData.connectorType,
                            mouseEvent: {
                                clientX: e.clientX,
                                clientY: e.clientY,
                                layerX: e.layerX,
                                layerY: e.layerY
                            }
                        }
                    });
                    window.dispatchEvent(customEvent);
                    console.log('[DEBUG] Dispatched connectorSelected event with outputid:', connectorGroup.userData.outputid);
                }

                deps.updateThreeFrame();
                return; // Prevent further processing for connector sprite clicks
            }
        }

        // NOTE: Face center sphere and sprite handling has been moved to editor.component.ts processObjectSelection method
        // to avoid conflicts and ensure proper scene action menu setup

        // Check if this is a face center object that should be handled by editor component
        if (firstIntersectedObject &&
            ((firstIntersectedObject instanceof THREE.Mesh && firstIntersectedObject.userData.type === 'refpoint' && firstIntersectedObject.userData.isFaceCenter) ||
                (firstIntersectedObject instanceof THREE.Sprite && firstIntersectedObject.userData.type === 'refpoint'))) {
            console.log('[DEBUG] Face center object detected - skipping InteractionManager processing as it\'s handled by editor component');
            return; // Let the editor component handle face center clicks
        }

        // Check for connector objects before continuing with general object selection
        if (firstIntersectedObject && (firstIntersectedObject.userData?.isConnector || firstIntersectedObject.userData?.isConnectorArrow || firstIntersectedObject.userData?.isConnectorSphere || firstIntersectedObject.parent?.userData?.isConnector)) {
            console.log('[DEBUG] Processing connector object double-click');

            // Get the connector group (either the object itself or its parent)
            const connectorGroup = firstIntersectedObject.userData?.isConnector ? firstIntersectedObject : firstIntersectedObject.parent;

            if (connectorGroup && connectorGroup.userData?.isConnector) {
                console.log('[DEBUG] Found connector group:', connectorGroup.userData.connectorLabel);

                // Emit connector selection event with mouse coordinates
                if (typeof window !== 'undefined' && (window as any).dispatchEvent) {
                    const customEvent = new CustomEvent('connectorSelected', {
                        detail: {
                            label: connectorGroup.userData.connectorLabel,
                            outputid: connectorGroup.userData.outputid,
                            position: connectorGroup.userData.position,
                            normal: connectorGroup.userData.normal,
                            type: connectorGroup.userData.connectorType,
                            mouseEvent: {
                                clientX: e.clientX,
                                clientY: e.clientY,
                                layerX: e.layerX,
                                layerY: e.layerY
                            }
                        }
                    });
                    window.dispatchEvent(customEvent);
                    console.log('[DEBUG] Dispatched connectorSelected event with outputid:', connectorGroup.userData.outputid);
                }

                deps.updateThreeFrame();
                return; // Prevent further processing for connector clicks
            }
        }

        // Continue with object selection for non-sprites
        console.log('[DEBUG] Processing non-sprite object selection');

        // Ensure any existing inputSprite is removed and reference cleared *before* processing new clicks
        if (deps.inputSprite) {
            deps.scene.remove(deps.inputSprite);
            deps.setInputSprite(null); // Explicitly nullify the reference
        }
        deps.setInputPoint(null); // Reset inputPoint as well

        // Check for dimension text sprite click first
        for (var i = 0; i < intersects?.length; i++) {
            if (intersects[i].object?.userData?.isDimensionText) {
                await deps.handleDimensionTextClick(intersects[i].object);
                return;
            }
        }

        console.log('sceneClick', e);
        deps.setSheetSelected(false);

        var lastSelectedNode = deps.selectedNode;
        deps.setSelectedNode(null);
        deps.setSheetNode(null);

        // Use the same intersects from above, no need to re-cast
        console.log('[DEBUG] Using existing intersects for object selection:', intersects?.length);

        console.log('[DEBUG] *** STARTING MAIN OBJECT PROCESSING ***');
        console.log('[DEBUG] intersects.length:', intersects?.length);

        if (intersects?.length > 0) {
            let copied = false;

            console.log('[DEBUG] *** CHECKING FOR OUTPUTID OBJECTS ***');
            // First check for objects with outputid and index (but exclude sprites and face center spheres which were already handled)
            for (var i = 0; i < intersects?.length; i++) {
                const obj = intersects[i].object;
                // Exclude sprites and face center spheres since they're handled above
                const isFaceCenterSphere = obj instanceof THREE.Mesh && obj.userData?.type === 'refpoint' && obj.userData?.isFaceCenter;
                if (!(obj instanceof THREE.Sprite) && !isFaceCenterSphere && obj?.userData?.outputid && obj?.userData?.index !== undefined) {
                    const outputid = obj.userData.outputid;
                    const index = obj.userData.index;
                    const textToCopy = outputid + "." + index;
                    copied = true;
                    console.log('[DEBUG] Found object with outputid.index:', textToCopy);
                    await deps.excels[deps.PROJECT].clearClipboard();
                    deps.setInputPosition(obj.position);
                    deps.setInputSprite(obj.clone() as any);

                    // Clone material if the object has one (e.g., Mesh objects)
                    if ('material' in obj && obj.material) {
                        deps.inputSprite.material = (obj.material as any).clone();
                        deps.inputSprite.userData.type = "inputsprite";
                        deps.inputSprite.material.color.set(0xff0000); // Set color to red
                    } else {
                        deps.inputSprite.userData.type = "inputsprite";
                    }

                    deps.scene.add(deps.inputSprite);
                    deps.setInputPoint(textToCopy);

                    navigator.clipboard.writeText(textToCopy).then(() => {
                        deps.showClipboardLabelWithText(textToCopy);
                    });
                    if (copied) return;
                }
            }

            // Then look for regular objects for selection (excluding sprites and helpers)
            console.log('[DEBUG] *** STARTING OBJECT SELECTION LOOP ***');
            console.log('[DEBUG] Total intersects to check:', intersects?.length);

            var obj = null;
            for (var i = 0; i < intersects?.length; i++) {
                const intersectedObj = intersects[i].object;
                console.log(`[DEBUG] *** CHECKING INTERSECT ${i}/${intersects.length} ***`);
                console.log('[InteractionManager] Checking intersected object for selection:', {
                    name: intersectedObj.name,
                    type: intersectedObj.constructor.name,
                    isSprite: intersectedObj instanceof THREE.Sprite,
                    hasUserData: !!intersectedObj.userData,
                    isHelper: intersectedObj.userData?.isHelper,
                    visible: intersectedObj.visible
                });

                const isSprite = intersectedObj instanceof THREE.Sprite;
                const isHelper = intersectedObj.userData?.isHelper;
                const isBaseScene = intersectedObj.name === "baseScene";
                const isHemiLight = intersectedObj.name === "hemilighthelper";
                const isVisible = intersectedObj.visible;

                console.log(`[DEBUG] Selection criteria check:`, {
                    isSprite, isHelper, isBaseScene, isHemiLight, isVisible,
                    passesCheck: !isSprite && !isHelper && !isBaseScene && !isHemiLight && isVisible
                });

                if (!isSprite && !isHelper && !isBaseScene && !isHemiLight && isVisible) {
                    obj = intersectedObj;
                    console.log('[DEBUG] *** OBJECT SELECTED! ***');
                    console.log('[InteractionManager] Found selectable object:', obj.name || 'unnamed');

                    // IMMEDIATE OBJECT MENU CALL - before any complex logic
                    console.log('[DEBUG] *** IMMEDIATELY CALLING updateSelectedObjectHelpe ***');
                    deps.setSelectedObject(obj);

                    if (deps.editorComponent && typeof deps.editorComponent.updateSelectedObjectHelpe === 'function') {
                        console.log('[DEBUG] *** CALLING updateSelectedObjectHelpe RIGHT NOW ***');
                        deps.editorComponent.updateSelectedObjectHelpe();
                        console.log('[DEBUG] *** updateSelectedObjectHelpe call completed immediately ***');
                    } else {
                        console.warn('[DEBUG] *** editorComponent not available for immediate call ***');
                    }

                    console.log('[InteractionManager] FULL SELECTED OBJECT:', {
                        name: obj.name,
                        uuid: obj.uuid,
                        type: obj.constructor.name,
                        userData: obj.userData,
                        material: obj.material ? {
                            type: obj.material.constructor.name,
                            color: obj.material.color,
                            opacity: obj.material.opacity
                        } : null,
                        geometry: obj.geometry ? {
                            type: obj.geometry.constructor.name,
                            attributes: Object.keys(obj.geometry.attributes || {})
                        } : null,
                        parent: obj.parent ? {
                            name: obj.parent.name,
                            type: obj.parent.constructor.name,
                            userData: obj.parent.userData
                        } : null,
                        position: obj.position.toArray(),
                        rotation: obj.rotation.toArray(),
                        scale: obj.scale.toArray(),
                        visible: obj.visible,
                        layers: obj.layers.mask,
                        castShadow: obj.castShadow,
                        receiveShadow: obj.receiveShadow
                    });
                    break;
                } else {
                    console.log(`[DEBUG] *** OBJECT REJECTED *** (failed criteria)`);
                }
            }

            console.log('[DEBUG] *** OBJECT SELECTION LOOP COMPLETE ***');
            console.log('[DEBUG] Final selected obj:', obj ? obj.name : 'null');

            if (obj) {
                var parent = obj.parent;

                // gltf node?
                while (obj?.userData?.isGltf) {
                    obj = obj.parent;
                    parent = obj;
                }

                // übergeordnete projekte nicht selektiert?
                // IMPROVED: Handle case where parent might be null or have empty name
                var nname = "";
                if (parent && parent.name) {
                    nname = parent.name.replace(deps.selectedprojectpath, "");
                }

                console.log('[InteractionManager] Name analysis:', {
                    objName: obj.name || 'empty',
                    parentName: parent?.name || 'no parent',
                    selectedprojectpath: deps.selectedprojectpath,
                    nname: nname,
                    parentExists: !!parent
                });

                // IMPROVED: Better logic for handling empty names and already selected objects
                if (nname == "" && parent && parent.name) {
                    console.log('[InteractionManager] Parent name matches selected path exactly - checking for valid selection');

                    // Check if this object has valid userData for spreadsheet selection
                    if (obj.userData?.sheetid == deps.getSheetid() && obj.userData?.rowindex !== undefined) {
                        console.log('[InteractionManager] Object has valid sheet data, processing selection');
                        deps.hot.selectCell(obj.userData.rowindex, 2);
                        if (deps.hot.getSelectedLast()[0] == obj.userData.rowindex)
                            deps.handleObjectSelectionByRows(obj.userData.rowindex);

                        // Set selected object and return
                        deps.setSelectedObject(obj);
                        console.log('[InteractionManager] Selected object with valid userData:', obj.name || 'unnamed');
                        return;
                    }

                    // If no valid userData, still allow selection for highlighting
                    deps.setSelectedObject(obj);
                    console.log('[InteractionManager] Selected object for highlighting (no sheet data):', obj.name || 'unnamed');
                    return;
                }

                // object in scene
                if (nname == "root") {
                    console.log('[InteractionManager] Processing root object:', {
                        name: obj.name,
                        hasUserData: !!obj.userData,
                        hasCommentRow: !!obj.userData?.commentrow,
                        hasSheetId: obj.userData?.sheetid !== undefined,
                        hasRowIndex: obj.userData?.rowindex !== undefined
                    });

                    if (obj.userData?.commentrow?.indexOf("ONCLICK") > -1) {
                        // Handle onClick events if needed
                    }

                    if (obj.userData?.sheetid == deps.getSheetid() && obj.userData?.rowindex !== undefined) {
                        deps.hot.selectCell(obj.userData.rowindex, 2);
                        if (deps.hot.getSelectedLast()[0] == obj.userData.rowindex)
                            deps.handleObjectSelectionByRows(obj.userData.rowindex);
                    }
                    console.log("obj", obj);
                    return;
                }

                // Enhanced logic for nested subprojects
                // Instead of just taking the first project after the selected path,
                // we need to find the deepest project level that we can select
                console.log('[InteractionManager] ===== ANALYZING PROJECT HIERARCHY =====');
                console.log('[InteractionManager] parent.name:', parent.name);
                console.log('[InteractionManager] selectedprojectpath:', deps.selectedprojectpath);
                console.log('[InteractionManager] nname (after selectedpath removal):', nname);

                // Parse all project segments in the hierarchy
                const allSegments = parent.name.split('#');
                const selectedSegments = deps.selectedprojectpath.split('#');

                console.log('[InteractionManager] allSegments:', allSegments);
                console.log('[InteractionManager] selectedSegments:', selectedSegments);

                // Find all project segments that come after the currently selected path
                const unselectedProjectSegments = [];
                for (let i = selectedSegments.length; i < allSegments.length; i++) {
                    const segment = allSegments[i];
                    if (segment && segment.includes('project')) {
                        const projectMatch = segment.match(/^([^$^]+)/);
                        if (projectMatch) {
                            unselectedProjectSegments.push({
                                index: i,
                                projectId: projectMatch[1],
                                fullSegment: segment
                            });
                        }
                    }
                }

                console.log('[InteractionManager] unselectedProjectSegments:', unselectedProjectSegments);

                // Find the deepest project that we can actually select
                let targetProject = null;
                let targetPath = null;

                // Try from the deepest level backwards
                for (let i = unselectedProjectSegments.length - 1; i >= 0; i--) {
                    const projectInfo = unselectedProjectSegments[i];

                    // Build the path up to this project level
                    let testPath = selectedSegments.join('#');

                    // Add all intermediate project segments up to this one
                    for (let j = 0; j <= i; j++) {
                        const intermediateProject = unselectedProjectSegments[j];
                        if (!testPath.endsWith("^$" + deps.getSheetid())) {
                            testPath += "^$" + deps.getSheetid();
                        }
                        testPath += "#" + intermediateProject.projectId;

                        // If this is our target level, test if the object exists
                        if (j === i) {
                            console.log('[InteractionManager] Testing deep project path:', testPath);
                            const testObj = deps.scene.getObjectByName(testPath);
                            if (testObj && testObj.userData?.typ?.toUpperCase().startsWith('PROJECT')) {
                                console.log('[InteractionManager] ✓ Found selectable deep project:', testPath);
                                targetProject = testObj;
                                targetPath = testPath;
                                break;
                            } else {
                                console.log('[InteractionManager] ✗ Deep project not found or not selectable:', testPath);
                            }
                        }
                    }

                    if (targetProject) break;
                }

                // If we found a deeper target, use it; otherwise fall back to the immediate next level
                if (targetProject && targetPath) {
                    console.log('[InteractionManager] Using deep nested project:', targetPath);
                    deps.selectedprojectpath_setter(targetPath);
                    parent = targetProject;
                } else {
                    // Fall back to original logic for immediate next level
                    console.log('[InteractionManager] Falling back to immediate next level selection');
                    var n = "#" + nname?.split('#')[1]?.split('^')[0];
                    var nname2 = deps.selectedprojectpath + n;
                    if (!deps.selectedprojectpath?.endsWith("^$" + deps.getSheetid()))
                        nname2 = deps.selectedprojectpath + "^$" + deps.getSheetid() + n;

                    console.log('[InteractionManager] Attempting to find object with path:', nname2);
                    var nobj = deps.scene.getObjectByName(nname2);

                    if (nobj) {
                        console.log('[InteractionManager] Found object with immediate fallback:', nobj.name);
                        deps.selectedprojectpath_setter(nname2);
                        parent = nobj;
                    } else {
                        console.log('[InteractionManager] Object not found with immediate fallback, trying alternative path construction');

                        // ISSUE 3: Improved fallback logic for subproject selection
                        // Try different path construction approaches
                        const alternativePaths = [
                            parent.name, // Use the full parent name as-is
                            deps.selectedprojectpath + "#" + nname?.split('#')[1], // Try without ^$ separator
                            "root^$" + deps.getSheetid() + "#" + nname?.split('#')[1]?.split('^')[0] // Try root-based path
                        ];

                        for (const altPath of alternativePaths) {
                            if (altPath && altPath !== nname2) {
                                console.log('[InteractionManager] Trying alternative path:', altPath);
                                const altObj = deps.scene.getObjectByName(altPath);
                                if (altObj && altObj.userData?.typ?.toUpperCase().startsWith('PROJECT')) {
                                    console.log('[InteractionManager] Found object with alternative path:', altObj.name);
                                    deps.selectedprojectpath_setter(altPath);
                                    parent = altObj;
                                    break;
                                }
                            }
                        }
                    }
                }

                // project, sheet selected
                if (parent?.userData?.typ?.toUpperCase().startsWith('PROJECT') || parent?.userData?.typ?.toUpperCase().startsWith('SHEET')) {
                    console.log('[InteractionManager] Processing project/sheet parent:', {
                        name: parent.name,
                        type: parent.userData?.typ,
                        hasUserData: !!parent.userData,
                        hasSheetId: parent.userData?.sheetid !== undefined,
                        hasRowIndex: parent.userData?.rowindex !== undefined,
                        hasRow: !!parent.userData?.row,
                        hasCommentRow: !!parent.userData?.commentrow
                    });

                    if (parent.userData?.sheetid == deps.getSheetid() && parent.userData?.rowindex !== undefined) {
                        deps.hot.selectCell(parent.userData.rowindex, 2);
                    }

                    if (parent.userData?.row && parent.userData?.commentrow) {
                        var params = Utils.mapRowToParams(parent.userData.row, parent.userData.commentrow);
                        if (params['selectable'] == false) {
                            console.log('selectable', params['selectable']);
                            return;
                        }
                    } else {
                        console.log('[InteractionManager] Parent missing row or commentrow data, allowing selection');
                    }

                    if (parent?.userData?.typ?.toUpperCase().startsWith('PROJECT')) {
                        if (parent.visible) {
                            deps.setSelectedNode(parent);

                            // Debug: log the parent's userData to see what we have
                            console.log('[InteractionManager] PROJECT parent userData:', {
                                subproject: parent.userData?.subproject,
                                name: parent.name,
                                typ: parent.userData?.typ,
                                allUserData: parent.userData
                            });

                            // Extract project name from the object name instead of relying on userData.subproject
                            // Name format: "root^$0#project42^$0" -> extract "project42"
                            let extractedProjectName = null;
                            if (parent.name) {
                                const nameMatch = parent.name.match(/#([^#^]+)\^?\$/);
                                if (nameMatch && nameMatch[1]) {
                                    extractedProjectName = nameMatch[1];
                                    console.log('[InteractionManager] Extracted project name from object name:', extractedProjectName);
                                }
                            }

                            // Use the extracted project name if userData.subproject is not available
                            const finalSubprojectID = parent.userData?.subproject || extractedProjectName;
                            console.log('[InteractionManager] Final subproject ID:', finalSubprojectID);

                            deps.setSelectedSubprojectID(finalSubprojectID);
                            deps.setSelectedProjectName(parent.name);
                            deps.selectedSubprojectPath = parent.name;

                            console.log('[InteractionManager] Setting isMainConfig to false for PROJECT selection');
                            deps.setIsMainConfig(false);

                            // Update JSON GUI for PROJECT selection - subproject GUI goes in jsongui, not jsongui2!
                            console.log('[InteractionManager] Updating JSON GUI for PROJECT selection, finalSubprojectID:', finalSubprojectID);

                            if (finalSubprojectID && finalSubprojectID !== deps.PROJECT) {
                                // Create GUI for the selected subproject and put it in jsongui
                                console.log('[InteractionManager] Creating JSON GUI for subproject:', finalSubprojectID);
                                const subprojectJsonGui = await deps.excels[finalSubprojectID].createJsonGUI(0);
                                console.log('[InteractionManager] Created subproject JSON GUI:', !!subprojectJsonGui, 'Structure:', subprojectJsonGui?.childs?.length || 0, 'children');

                                // Update jsongui with subproject GUI (jsongui2 stays null)
                                (deps as any).editorComponent.jsongui = subprojectJsonGui;
                                (deps as any).editorComponent.jsongui2 = null; // Make sure jsongui2 is clear
                                deps.triggerChangeDetection();
                                console.log('[InteractionManager] Subproject JSON GUI updated in jsongui, jsongui2 cleared');
                            } else {
                                // Create main project GUI
                                console.log('[InteractionManager] Creating main project JSON GUI');
                                const newJsonGui = await deps.excels[deps.PROJECT].createJsonGUI(deps.getSheetid());
                                console.log('[InteractionManager] Created main JSON GUI:', !!newJsonGui, 'Structure:', newJsonGui?.childs?.length || 0, 'children');

                                // Update jsongui for main project
                                (deps as any).editorComponent.jsongui = newJsonGui;
                                (deps as any).editorComponent.jsongui2 = null; // Make sure jsongui2 is clear
                                deps.triggerChangeDetection();
                                console.log('[InteractionManager] Main JSON GUI updated in jsongui');
                            }

                            // Update breadcrumbs for PROJECT selection
                            deps.updateBreadcrumbs();
                        }
                    }

                    if (parent?.userData?.typ?.toUpperCase().startsWith('SHEET')) {
                        console.log('[InteractionManager] SHEET detected:', {
                            name: parent.name,
                            typ: parent.userData?.typ,
                            visible: parent.visible
                        });

                        if (parent?.visible) {
                            deps.setSelectedNode(parent);
                            console.log('[InteractionManager] Set selectedNode to sheet:', parent.name);
                        }
                        deps.setSheetNode(parent);
                        if (!deps.selectedNode) {
                            deps.setSelectedNode(parent);
                            console.log('[InteractionManager] Force set selectedNode to sheet:', parent.name);
                        }
                        console.log('[InteractionManager] After setting, selectedNode is:', deps.selectedNode?.name);
                    }

                    // Update material opacity for scene traversal
                    deps.scene.traverse((child) => {
                        if (child instanceof THREE.Mesh && child.material) {
                            if (child.userData?.isGltf) {
                                (child.material as any).format = THREE.RGBAFormat;
                            }
                            child.material.transparent = true;
                            if (!child.userData) child.userData = {};
                            // Store original opacity if not already stored
                            if (child.userData.opacity === undefined) {
                                child.userData.opacity = child.material.opacity;
                            }
                            child.material.opacity = 0.1;
                        }
                    });

                    // Restore normal opacity for selected nodes and all their children
                    const nodesToRestore = [];

                    // Always include the current parent (PROJECT or SHEET)
                    if (parent) {
                        nodesToRestore.push(parent);
                        console.log('[InteractionManager] Added parent to restore list:', parent.name, parent.userData?.typ);
                    }

                    // Include selectedNode if different from parent
                    if (deps.selectedNode && deps.selectedNode !== parent) {
                        nodesToRestore.push(deps.selectedNode);
                        console.log('[InteractionManager] Added selectedNode to restore list:', deps.selectedNode.name);
                    }

                    // Include sheetNode if different from others
                    if (deps.sheetNode && !nodesToRestore.includes(deps.sheetNode)) {
                        nodesToRestore.push(deps.sheetNode);
                        console.log('[InteractionManager] Added sheetNode to restore list:', deps.sheetNode.name);
                    }

                    // For SHEET selections within subprojects, also restore the subproject parent
                    if (parent?.userData?.typ?.toUpperCase().startsWith('SHEET') && deps.selectedSubprojectPath && !deps.isMainConfig) {
                        const subprojectNode = deps.scene.getObjectByName(deps.selectedSubprojectPath);
                        if (subprojectNode && !nodesToRestore.includes(subprojectNode)) {
                            nodesToRestore.push(subprojectNode);
                            console.log('[InteractionManager] Added subproject parent to restore list for sheet:', subprojectNode.name);
                        }
                    }

                    console.log('[InteractionManager] Total nodes to restore opacity:', nodesToRestore.length, nodesToRestore.map(n => n.name));

                    // Restore opacity for all selected nodes
                    nodesToRestore.forEach((node, index) => {
                        console.log(`[InteractionManager] Restoring opacity for node ${index + 1}/${nodesToRestore.length}:`, node.name);
                        node.traverse((child) => {
                            if (child instanceof THREE.Mesh && child.material) {
                                // Restore original opacity
                                const originalOpacity = child.userData?.opacity !== undefined ? child.userData.opacity : 1.0;
                                child.material.opacity = originalOpacity;

                                // Set transparency based on original opacity
                                if (originalOpacity >= 0.99) {
                                    child.material.transparent = false;
                                } else {
                                    child.material.transparent = true;
                                }
                            }
                        });
                    });

                    deps.updateThreeFrame();

                    console.log('[InteractionManager] After updateThreeFrame, checking for SHEET processing again');
                    if (parent?.userData?.typ?.toUpperCase().startsWith('SHEET')) {
                        console.log('[InteractionManager] Processing SHEET selection:', {
                            name: parent.name,
                            typ: parent.userData?.typ,
                            visible: parent.visible,
                            selectedNode: deps.selectedNode?.name
                        });

                        if (parent.visible) {
                            console.log('[InteractionManager] Sheet is visible, proceeding with selection');
                            deps.setSheetSelected(true);

                            // Try to get sheet ID from selectedNode first, then from parent
                            var sid = deps.selectedNode?.userData?.sheetid || parent.userData?.sheetid;
                            console.log('[InteractionManager] Sheet ID (sid):', sid, 'from selectedNode:', deps.selectedNode?.userData?.sheetid, 'from parent:', parent.userData?.sheetid);
                            if (parent.userData?.sourcesheetid == deps.getSheetid() && parent.userData?.rowindex !== undefined) {
                                deps.hot.selectCell(parent.userData.rowindex, 2);
                            }

                            if (parent.userData?.row && parent.userData?.commentrow) {
                                var vals = Utils.mapRowToParams(parent.userData.row, parent.userData.commentrow);
                                deps.setUihead(parent.userData.row[0] || '');

                                // Use selectedNode if available, otherwise use parent
                                const nodeToUse = deps.selectedNode || parent;
                                console.log('[InteractionManager] Using node for JSON GUI creation:', nodeToUse.name, 'hasCommentRow:', !!nodeToUse.userData?.commentrow, 'hasRow:', !!nodeToUse.userData?.row);

                                if (sid !== undefined && nodeToUse.userData?.commentrow && nodeToUse.userData?.row) {
                                    await deps.excels[deps.PROJECT].setConfiugartionsParams(sid, nodeToUse.userData.commentrow, nodeToUse.userData.row);
                                    const sheetJsonGui = await deps.excels[deps.PROJECT].createJsonGUI(sid);
                                    console.log('[InteractionManager] Created sheet JSON GUI for sid:', sid, 'Structure:', sheetJsonGui?.childs?.length || 0, 'children');

                                    // Update jsongui2 with sheet GUI
                                    (deps as any).editorComponent.jsongui2 = sheetJsonGui;
                                    deps.triggerChangeDetection();
                                    console.log('[InteractionManager] Sheet JSON GUI updated in jsongui2');

                                    // Update breadcrumbs for SHEET selection
                                    deps.updateBreadcrumbs();
                                }

                                var newcss = deps.project.configurations[deps.selectedNode?.userData?.rowindex - 1]?.csscode;
                                deps.csschange(newcss, true);
                            } else {
                                console.log('[InteractionManager] SHEET parent missing userData properties');
                            }

                            deps.setSelectedObject(obj);
                            // Keep the sheet parent as selectedNode, not the clicked child object
                            // The selectedNode should remain the SHEET object for proper transparency handling
                            if (!deps.selectedNode) {
                                deps.setSelectedNode(parent);
                            }
                            console.log('selectedObject1', deps.selectedObject);
                            console.log('selectedNode', deps.selectedNode);
                        }
                        return;
                    } else {
                        // subproject/subconfiguration selected
                        await deps.subprojectSelected();
                        console.log('selectedObject2', deps.selectedObject);
                        return;
                    }
                }
            }

            deps.setSelectedObject(obj);
        }

        deps.setSelectedObject(obj);

        // Clear all selection highlights if no object was selected (clicking on empty space)
        if (!obj) {
            console.log('[InteractionManager] No object selected - clearing all highlights');
            this.clearAllSelectionHighlights();
        }

        console.log('[InteractionManager] FINAL SELECTED OBJECT:', obj ? {
            name: obj.name,
            uuid: obj.uuid,
            type: obj.constructor.name,
            userData: obj.userData,
            material: obj.material ? {
                type: obj.material.constructor.name,
                color: obj.material.color,
                opacity: obj.material.opacity
            } : null,
            geometry: obj.geometry ? {
                type: obj.geometry.constructor.name,
                attributes: Object.keys(obj.geometry.attributes || {})
            } : null,
            parent: obj.parent ? {
                name: obj.parent.name,
                type: obj.parent.constructor.name,
                userData: obj.parent.userData
            } : null,
            position: obj.position.toArray(),
            rotation: obj.rotation.toArray(),
            scale: obj.scale.toArray(),
            visible: obj.visible,
            layers: obj.layers.mask,
            castShadow: obj.castShadow,
            receiveShadow: obj.receiveShadow
        } : null);

        console.log('[DEBUG] *** REACHED FINAL OBJECT DEBUG SECTION ***');
        console.log('[DEBUG] obj exists:', !!obj);
        console.log('[DEBUG] obj name:', obj?.name);

        // CRITICAL DEBUG: Check if object is being set and if updateSelectedObjectHelpe is called
        if (obj) {
            console.log('[DEBUG] *** OBJECT FOUND! Setting selectedObject and calling updateSelectedObjectHelpe ***');
            deps.setSelectedObject(obj);
            console.log('[DEBUG] After setSelectedObject, deps.selectedObject is:', deps.selectedObject?.name);

            // Check editor component availability
            console.log('[DEBUG] deps.editorComponent exists:', !!deps.editorComponent);
            console.log('[DEBUG] updateSelectedObjectHelpe method exists:', !!(deps.editorComponent && typeof deps.editorComponent.updateSelectedObjectHelpe === 'function'));

            // FORCE CALL updateSelectedObjectHelpe
            if (deps.editorComponent && typeof deps.editorComponent.updateSelectedObjectHelpe === 'function') {
                console.log('[DEBUG] *** CALLING updateSelectedObjectHelpe directly ***');
                deps.editorComponent.updateSelectedObjectHelpe();
                console.log('[DEBUG] *** updateSelectedObjectHelpe call completed ***');
            } else {
                console.warn('[DEBUG] *** editorComponent or updateSelectedObjectHelpe method not available ***');
                console.log('[DEBUG] deps keys:', Object.keys(deps));
            }
        } else {
            console.log('[DEBUG] *** NO OBJECT FOUND in final selection ***');
        }
        console.log('selectedObject', deps.selectedObject);

        // zurück zum Hauptprojekt ------
        // werte in zelle setzen, wenn rootproject
        if (lastSelectedNode?.parent?.name == "root" || lastSelectedNode?.name == "root" || lastSelectedNode === deps.scene || selectRoot) {
            try {
                var cellchanges = [];
                console.log('[InteractionManager] Processing lastSelectedNode for cell updates:', {
                    hasLastSelectedNode: !!lastSelectedNode,
                    hasUserData: !!lastSelectedNode?.userData,
                    hasRow: !!lastSelectedNode?.userData?.row,
                    hasRowIndex: lastSelectedNode?.userData?.rowindex !== undefined,
                    hasSheetId: lastSelectedNode?.userData?.sheetid !== undefined
                });

                if (lastSelectedNode?.userData?.row &&
                    lastSelectedNode?.userData?.rowindex !== undefined &&
                    lastSelectedNode?.userData?.sheetid !== undefined) {

                    for (var i = 0; i < lastSelectedNode.userData.row.length; i++) {
                        var cellval = lastSelectedNode.userData.row[i];

                        // formelnn nicht übernehmen
                        var oldcellval = deps.cellformulas[lastSelectedNode.userData.rowindex][i];
                        if (typeof oldcellval === 'string' && oldcellval.startsWith("=")) {
                            cellval = oldcellval;
                            break;
                        }
                        var tc = await deps.excels[deps.PROJECT].setCellContents({ sheet: lastSelectedNode.userData.sheetid, row: lastSelectedNode.userData.rowindex, col: i }, cellval);
                        cellchanges.push(...tc);
                        deps.setIsMainConfig(true);
                    }
                    deps.cellChanges2dataset(cellchanges);

                    await deps.updateModel(cellchanges, null, deps.scene);

                    if (deps.isEditMode)
                        deps.hot.render();
                } else {
                    console.log('[InteractionManager] Skipping cell updates due to missing userData properties');
                }
            } catch (error) {
                console.warn('updateModel', error);
            }
        }

        if (deps.selectedObjectHelper)
            deps.scene.remove(deps.selectedObjectHelper);
        deps.setSelectedNode(null);
        deps.setSelectedSubprojectID(deps.PROJECT);
        deps.setSelectedProjectName(null);
        deps.selectedSubprojectPath = null;
        deps.selectedprojectpath_setter("root^$" + deps.getSheetid());

        console.log('[InteractionManager] Setting isMainConfig to true - returning to main project');
        deps.setIsMainConfig(true);
        deps.setJsongui2(null);

        // Clear dataset2 when returning to main config
        console.log('[InteractionManager] Clearing dataset2 when returning to main config');
        deps.setDataset2(null);

        deps.scene.traverse((child) => {
            if (child instanceof THREE.Mesh && child.material) {
                // Restore original opacity for all objects when returning to main scene
                const originalOpacity = child.userData?.opacity !== undefined ? child.userData.opacity : 1.0;
                child.material.opacity = originalOpacity;

                // Set transparency based on original opacity
                if (originalOpacity >= 0.99) {
                    child.material.transparent = false;
                } else {
                    child.material.transparent = true;
                }
            }
        });

        const mainJsonGui = await deps.excels[deps.PROJECT].createJsonGUI(deps.getSheetid());
        console.log('[InteractionManager] Created main JSON GUI for return to main:', !!mainJsonGui, 'Structure:', mainJsonGui?.childs?.length || 0, 'children');

        // Update directly on the component and clear subproject GUI
        (deps as any).editorComponent.jsongui = mainJsonGui;
        (deps as any).editorComponent.jsongui2 = null; // Clear subproject GUI
        deps.triggerChangeDetection();
        deps.csschange(deps.project.csscode);
        console.log('[InteractionManager] Returned to main project - cleared jsongui2 and updated jsongui');

        // Update breadcrumbs when returning to main
        deps.updateBreadcrumbs();

        try {
            var userinputs = JSON.parse(JSON.stringify(deps.project?.configmodel?.userinputs));
            deps.project.configmodel.userinputs = userinputs;
        } catch { }

        var configModel = await deps.createConfigModel();
        deps.project.configmodel = configModel;

        await deps.subprojectSelected();
        deps.updateThreeFrame();

        console.log('[InteractionManager] sceneClick completed successfully', {
            finalSelectedNode: deps.selectedNode?.name,
            finalSelectedSubprojectID: deps.selectedSubprojectID,
            finalSelectedprojectpath: deps.selectedprojectpath,
            finalIsMainConfig: deps.isMainConfig,
            sceneChildrenCount: deps.scene.children.length,
            processingTime: Date.now() - (e as any).timestamp || 'unknown'
        });
        console.log("scene", deps.scene);
    }

    async subprojectSelected() {
        console.log('[InteractionManager] subprojectSelected called');

        if (!this.dependencies) {
            console.warn('[InteractionManager] subprojectSelected: Dependencies not set');
            return;
        }

        const deps = this.dependencies;

        if (deps.selectedSubprojectID && deps.selectedSubprojectID !== deps.PROJECT) {
            try {
                console.log('[InteractionManager] Processing subproject selection:', {
                    selectedSubprojectID: deps.selectedSubprojectID,
                    PROJECT: deps.PROJECT,
                    hasSelectedNode: !!deps.selectedNode,
                    hasUserData: !!deps.selectedNode?.userData
                });

                // Ensure we have the required data before proceeding
                if (!deps.selectedNode?.userData?.commentrow || !deps.selectedNode?.userData?.row) {
                    console.warn('[InteractionManager] Missing required userData for subproject selection');
                    return;
                }

                await deps.excels[deps.selectedSubprojectID].setConfiugartionsParams(0, deps.selectedNode.userData.commentrow, deps.selectedNode.userData.row);
                var subsheet = await deps.excels[deps.selectedSubprojectID].getSheetByIndex(0);

                // Ensure subsheet has valid data before setting dataset2
                if (subsheet && subsheet.cells) {
                    console.log('[InteractionManager] Setting dataset2 with subsheet data:', {
                        cellsLength: subsheet.cells.length,
                        firstRowLength: subsheet.cells[0]?.length
                    });
                    deps.setDataset2(subsheet.cells);

                    // Also handle cellformulas if needed for the subproject
                    if (subsheet.cellvalues && deps.setCellformulas) {
                        console.log('[InteractionManager] Also setting cellformulas for subproject');
                        // Note: We might need to store subproject cellformulas separately
                        // For now, we'll rely on the main cellformulas
                    }
                } else {
                    console.error('[InteractionManager] Invalid subsheet data received');
                    return;
                }

                deps.setUihead(deps.selectedNode.userData.row[0]);

                console.log('[InteractionManager] Note: jsonGUI2 for subproject should already be created in main selection logic');

                var newcss = deps.project.configurations[deps.selectedNode.userData.rowindex - 1]?.csscode;
                deps.csschange(newcss, true);

                console.log('[InteractionManager] Subproject configuration applied successfully');
            } catch (error) {
                console.error('[InteractionManager] subprojectSelected error:', error);
                // Reset to main config on error
                deps.setIsMainConfig(true);
                deps.setDataset2(null);
            }
        } else {
            // If we're not in a subproject, make sure dataset2 is cleared
            console.log('[InteractionManager] Not in subproject, clearing dataset2');
            deps.setDataset2(null);
        }

        // CONNECTORS SHOULD ALWAYS REMAIN VISIBLE - DO NOT HIDE THEM!
        deps.scene?.traverse((child) => {
            // Handle connectors - KEEP THEM ALWAYS VISIBLE
            if (child.userData?.isConnector) {
                console.log('[InteractionManager] CONNECTOR PROTECTION: Ensuring connector remains visible:', child.userData.connectorLabel);
                child.visible = true; // FORCE ALWAYS VISIBLE

                // Ensure connector children are also visible
                child.traverse((grandChild) => {
                    if (grandChild instanceof THREE.Sprite) {
                        grandChild.visible = true;
                        grandChild.renderOrder = 999;
                        if (grandChild.material instanceof THREE.SpriteMaterial) {
                            grandChild.material.depthTest = false;
                            grandChild.material.depthWrite = false;
                            grandChild.material.transparent = true;
                            grandChild.material.needsUpdate = true;
                        }
                    }
                    if (grandChild.userData?.isConnectorArrow || grandChild.userData?.isConnectorSphere) {
                        grandChild.visible = true;
                    }
                });

                // Force matrix updates
                child.updateMatrix();
                child.updateMatrixWorld(true);
                return; // Skip further processing for connectors
            }

            // Handle edges by checking for objects named "KANTE" or edges of cut objects
            if (child.name === "KANTE" || (child instanceof THREE.LineSegments && child.parent?.userData?.cuttedBy)) {
                // Get the parent mesh that owns this edge
                const parent = child.parent;
                if (parent) {
                    const isMainConfig = parent.name.startsWith("root^$" + deps.getSheetid() + "#") && !parent.name.includes("project");
                    const belongsToSelectedProject = parent === deps.selectedNode || deps.selectedNode?.getObjectById(parent.id) || (deps.selectedNode === null && deps.isMainConfig);

                    // Show edges if:
                    // 1. We're in main config and it's a main config object
                    // 2. We're in a subproject and the edge belongs to the selected project
                    // 3. We're deselecting (selectedSubprojectID === PROJECT) and it's a main config object
                    // 4. We're returning to the main scene
                    const shouldShowEdge =
                        (deps.isMainConfig) ||
                        (!deps.isMainConfig && belongsToSelectedProject) ||
                        (deps.selectedSubprojectID === deps.PROJECT && isMainConfig) ||
                        (deps.selectedNode === deps.scene);

                    child.visible = shouldShowEdge;
                    if ('material' in child && child.material) {
                        (child.material as any).visible = shouldShowEdge;
                        // Ensure edges of cut objects maintain proper opacity
                        if ('material' in parent && parent.material && parent.userData?.cuttedBy) {
                            (child.material as any).transparent = (parent.material as any).transparent;
                            (child.material as any).opacity = (parent.material as any).opacity;
                        }
                    }
                }
            }
        });

        deps.updateThreeFrame();
        console.log('[InteractionManager] subprojectSelected completed');
    }

    dispose() {
        console.log('Disposing InteractionManager');
        this.container.removeEventListener('mousemove', this.handleMouseMove);
        this.container.removeEventListener('click', this.handleClick);
        this.container.removeEventListener('mousedown', this.handleMouseDown);
        this.container.removeEventListener('mouseup', this.handleMouseUp);
        this.container.removeEventListener('mouseleave', this.handleMouseLeave);
        this.container.removeEventListener('dblclick', this.handleSceneClick);
        this.interactiveObjects.clear();
        this.hoveredObject = null;
        this.selectedObject = null;
        this.dependencies = null;

        // Clean up hover highlighting
        this.handleObjectMouseOut();
        this.lastHoveredObject = null;

        // Reset drag state
        this.mouseDownPosition = null;
        this.isDragging = false;
    }

    getHoveredObject(): THREE.Object3D | null {
        return this.hoveredObject;
    }

    getSelectedObject(): THREE.Object3D | null {
        return this.selectedObject;
    }

    /**
     * Public method to handle object hovering from external components
     */
    public handleObjectHover(object: THREE.Object3D | null) {
        if (object) {
            this.handleObjectMouseOver(object);
        } else {
            this.handleObjectMouseOut();
        }
    }

    /**
     * Finds the next available row for inserting new output objects
     * Looks for the last non-empty output object row and adds a buffer
     */
    private findNextAvailableOutputRow(): number {
        const outputRow = SheetHelper.getOutputRow(this.dependencies.dataset);
        let lastObjectRow = outputRow;

        // Find the last row with actual output object data
        for (let i = outputRow + 1; i < this.dependencies.dataset.length; i++) {
            const row = this.dependencies.dataset[i];
            if (row && row[0] && row[0] !== '' && row[0] !== '#' &&
                row[1] && row[1] !== '' &&
                (this.isInAutocompleteList(row[1]) || row[1]?.startsWith('project'))) {
                lastObjectRow = i;
            }
        }

        // Return the row after the last object plus one empty row buffer
        return lastObjectRow + 2;
    }

} 